// @ts-nocheck
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import {
  HelpCircle,
  Plus,
  Search,
  Edit2,
  Trash2,
  Filter,
  ChevronLeft,
  ChevronRight,
  Eye,
} from 'lucide-react';
import {
  Card,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableHeader,
  TableCell,
  Button,
  Badge,
  Modal,
  ModalFooter,
  Input,
  Select,
  StatCard,
  CircularProgress,
} from '@azyntor-labs/nexus-ui';
import { questionsApi, questionBanksApi } from '@/api/endpoints';
import { Question, QuestionType, DifficultyLevel } from '@/types';
import { toast } from '@/utils/toast';

export const QuestionManagement = () => {
  const { bankId } = useParams<{ bankId: string }>();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [perPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [difficultyFilter, setDifficultyFilter] = useState<string>('');

  // Modal states
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState<Question | null>(null);

  // Form states
  const [formData, setFormData] = useState({
    question_bank_id: bankId || '',
    question_text: '',
    question_type: 'mcq' as QuestionType,
    options: [
      { id: '1', text: '', is_correct: false },
      { id: '2', text: '', is_correct: false },
      { id: '3', text: '', is_correct: false },
      { id: '4', text: '', is_correct: false },
    ],
    correct_answer: [] as string[],
    points: 1,
    difficulty: 'medium' as DifficultyLevel,
    tags: [] as string[],
    explanation: '',
  });

  // Fetch questions
  const { data: questionsResponse, isLoading } = useQuery({
    queryKey: ['questions', { page, per_page: perPage, question_bank_id: bankId, difficulty: difficultyFilter }],
    queryFn: () => questionsApi.list({
      page,
      per_page: perPage,
      question_bank_id: bankId,
      difficulty: difficultyFilter || undefined
    }),
    enabled: !!bankId,
  });

  // Fetch bank statistics
  const { data: bankStatsResponse } = useQuery({
    queryKey: ['questionBankStats', bankId],
    queryFn: async () => {
      const response = await questionBanksApi.getStatistics(bankId);
      return response;
    },
    enabled: !!bankId,
  });

  // Correctly extract questions array from nested response
  const questions = Array.isArray(questionsResponse?.data?.data?.data)
    ? questionsResponse.data.data.data
    : [];
  const total = (questionsResponse?.data?.data as any)?.total || 0;
  const totalPages = Math.ceil(total / perPage) || 1;

  // Filter questions by search term (only if search is active)
  const filteredQuestions = searchTerm
    ? questions.filter((question: Question) => {
        const searchLower = searchTerm.toLowerCase();
        return question.question_text.toLowerCase().includes(searchLower);
      })
    : questions;

  // Get stats from bank statistics API (for all questions, not just current page)
  const bankStats = bankStatsResponse?.data?.data || {};
  const totalQuestions = bankStats.total_questions || total;
  const easyCount = bankStats.by_difficulty?.easy || 0;
  const mediumCount = bankStats.by_difficulty?.medium || 0;
  const hardCount = bankStats.by_difficulty?.hard || 0;

  // Create question mutation
  const createQuestionMutation = useMutation({
    mutationFn: (data: Partial<Question>) => questionsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['questions'] });
      queryClient.invalidateQueries({ queryKey: ['questionBankStats', bankId] });
      toast.success('Question created successfully');
      setIsAddModalOpen(false);
      resetForm();
    },
    onError: (error: unknown) => {
      const message = error instanceof Error ? error.message : 'Failed to create question';
      toast.error(message);
    },
  });

  // Update question mutation
  const updateQuestionMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Question> }) =>
      questionsApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['questions'] });
      queryClient.invalidateQueries({ queryKey: ['questionBankStats', bankId] });
      toast.success('Question updated successfully');
      setIsEditModalOpen(false);
      setSelectedQuestion(null);
      resetForm();
    },
    onError: (error: unknown) => {
      const message = error instanceof Error ? error.message : 'Failed to update question';
      toast.error(message);
    },
  });

  // Delete question mutation
  const deleteQuestionMutation = useMutation({
    mutationFn: (id: string) => questionsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['questions'] });
      queryClient.invalidateQueries({ queryKey: ['questionBankStats', bankId] });
      toast.success('Question deleted successfully');
      setIsDeleteModalOpen(false);
      setSelectedQuestion(null);
    },
    onError: (error: unknown) => {
      const message = error instanceof Error ? error.message : 'Failed to delete question';
      toast.error(message);
    },
  });

  const resetForm = () => {
    setFormData({
      question_bank_id: bankId || '',
      question_text: '',
      question_type: 'mcq',
      options: [
        { id: '1', text: '', is_correct: false },
        { id: '2', text: '', is_correct: false },
        { id: '3', text: '', is_correct: false },
        { id: '4', text: '', is_correct: false },
      ],
      correct_answer: [],
      points: 1,
      difficulty: 'medium',
      tags: [],
      explanation: '',
    });
  };

  const handleAddQuestion = () => {
    setIsAddModalOpen(true);
    resetForm();
  };

  const handleEditQuestion = (question: Question) => {
    navigate(`/teacher/question-banks/${bankId}/questions/${question.id}/edit`);
  };

  const handleDeleteQuestion = (question: Question) => {
    setSelectedQuestion(question);
    setIsDeleteModalOpen(true);
  };

  const handleViewQuestion = (question: Question) => {
    setSelectedQuestion(question);
    setIsViewModalOpen(true);
  };

  const handleSubmitCreate = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.question_text) {
      toast.error('Please enter a question');
      return;
    }

    const dataToSubmit: Partial<Question> = {
      ...formData,
      options: formData.question_type === 'mcq' || formData.question_type === 'true_false'
        ? formData.options.filter(opt => opt.text.trim() !== '')
        : [],
      correct_answer: formData.question_type === 'mcq'
        ? formData.options.filter(opt => opt.is_correct).map(opt => opt.text)
        : formData.question_type === 'true_false'
        ? [formData.options.find(opt => opt.is_correct)?.text || '']
        : [],
    };

    createQuestionMutation.mutate(dataToSubmit);
  };

  const handleSubmitUpdate = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedQuestion) return;

    const dataToSubmit: Partial<Question> = {
      ...formData,
      options: formData.question_type === 'mcq' || formData.question_type === 'true_false'
        ? formData.options.filter(opt => opt.text.trim() !== '')
        : [],
      correct_answer: formData.question_type === 'mcq'
        ? formData.options.filter(opt => opt.is_correct).map(opt => opt.text)
        : formData.question_type === 'true_false'
        ? [formData.options.find(opt => opt.is_correct)?.text || '']
        : [],
    };

    updateQuestionMutation.mutate({ id: selectedQuestion.id, data: dataToSubmit });
  };

  const handleConfirmDelete = () => {
    if (!selectedQuestion) return;
    deleteQuestionMutation.mutate(selectedQuestion.id);
  };

  const handleOptionChange = (index: number, text: string) => {
    const newOptions = [...formData.options];
    newOptions[index] = { ...newOptions[index], text };
    setFormData({ ...formData, options: newOptions });
  };

  const handleCorrectChange = (index: number) => {
    const newOptions = [...formData.options];
    if (formData.question_type === 'true_false') {
      // For true/false, only one can be correct
      newOptions.forEach((opt, i) => {
        opt.is_correct = i === index;
      });
    } else {
      // For multiple choice, toggle
      newOptions[index] = { ...newOptions[index], is_correct: !newOptions[index].is_correct };
    }
    setFormData({ ...formData, options: newOptions });
  };

  const addOption = () => {
    const newOptions = [...formData.options];
    newOptions.push({
      id: `${newOptions.length + 1}`,
      text: '',
      is_correct: false,
    });
    setFormData({ ...formData, options: newOptions });
  };

  const removeOption = (index: number) => {
    if (formData.options.length <= 2) {
      toast.error('Must have at least 2 options');
      return;
    }
    const newOptions = formData.options.filter((_, i) => i !== index);
    setFormData({ ...formData, options: newOptions });
  };

  const questionTypeOptions = [
    { value: 'mcq', label: 'Multiple Choice' },
    { value: 'true_false', label: 'True/False' },
    { value: 'fill_blank', label: 'Fill in the Blank' },
    { value: 'match', label: 'Matching' },
    { value: 'short_answer', label: 'Short Answer' },
    { value: 'essay', label: 'Essay' },
    { value: 'coding', label: 'Coding' },
  ];

  const difficultyOptions = [
    { value: '', label: 'All Difficulties' },
    { value: 'easy', label: 'Easy' },
    { value: 'medium', label: 'Medium' },
    { value: 'hard', label: 'Hard' },
  ];

  const difficultyOptionsForForm = difficultyOptions.filter(opt => opt.value !== '');

  const getDifficultyBadgeVariant = (difficulty: DifficultyLevel) => {
    switch (difficulty) {
      case 'easy':
        return 'success';
      case 'medium':
        return 'warning';
      case 'hard':
        return 'danger';
      default:
        return 'secondary';
    }
  };

  const getQuestionTypeBadgeVariant = (type: QuestionType) => {
    switch (type) {
      case 'mcq':
        return 'primary';
      case 'true_false':
        return 'info';
      case 'short_answer':
        return 'warning';
      case 'essay':
        return 'secondary';
      case 'coding':
        return 'danger';
      default:
        return 'secondary';
    }
  };

  if (!bankId) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <HelpCircle className="w-16 h-16 text-white/30 mx-auto mb-4" />
          <p className="text-white/50">Please select a question bank to manage questions</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">Question Management</h1>
          <p className="text-white/50 mt-1">Create and manage questions in this bank</p>
        </div>
        <Button leftIcon={<Plus className="w-4 h-4" />} onClick={handleAddQuestion}>
          Add Question
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <StatCard
          title="Total Questions"
          value={totalQuestions}
          icon={<HelpCircle />}
          color="blue"
        />
        <StatCard
          title="Easy"
          value={easyCount}
          icon={<HelpCircle />}
          trendValue={totalQuestions > 0 ? `${Math.round((easyCount / totalQuestions) * 100)}%` : undefined}
          color="green"
          chart={<CircularProgress value={totalQuestions > 0 ? (easyCount / totalQuestions) * 100 : 0} size={48} />}
        />
        <StatCard
          title="Medium"
          value={mediumCount}
          icon={<HelpCircle />}
          trendValue={totalQuestions > 0 ? `${Math.round((mediumCount / totalQuestions) * 100)}%` : undefined}
          color="orange"
          chart={<CircularProgress value={totalQuestions > 0 ? (mediumCount / totalQuestions) * 100 : 0} size={48} />}
        />
        <StatCard
          title="Hard"
          value={hardCount}
          icon={<HelpCircle />}
          trendValue={totalQuestions > 0 ? `${Math.round((hardCount / totalQuestions) * 100)}%` : undefined}
          color="red"
          chart={<CircularProgress value={totalQuestions > 0 ? (hardCount / totalQuestions) * 100 : 0} size={48} />}
        />
      </div>

      {/* Filters and Search */}
      <Card>
        <div className="p-4 border-b border-white/[0.06]">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-white/40" />
              <Input
                placeholder="Search questions..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select
              options={difficultyOptions}
              value={difficultyFilter}
              onChange={(e) => {
                setDifficultyFilter(e.target.value);
                setPage(1);
              }}
            />
            <div className="flex items-center text-sm text-white/50">
              <Filter className="w-4 h-4 mr-2" />
              Showing {filteredQuestions.length} of {total} questions
            </div>
          </div>
        </div>

        {/* Questions Table */}
        {isLoading ? (
          <div className="text-center py-12">
            <div className="spinner mx-auto mb-4"></div>
            <p className="text-white/50">Loading questions...</p>
          </div>
        ) : filteredQuestions.length > 0 ? (
          <>
            <Table className="table-fixed w-full">
              <TableHead>
                <TableRow>
                  <TableHeader className="w-[40%]">Question</TableHeader>
                  <TableHeader className="w-[12%]">Type</TableHeader>
                  <TableHeader className="w-[12%]">Difficulty</TableHeader>
                  <TableHeader className="w-[8%]">Points</TableHeader>
                  <TableHeader className="w-[28%]">Actions</TableHeader>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredQuestions.map((question: Question) => (
                  <TableRow key={question.id}>
                    <TableCell className="w-[40%]">
                      <div className="overflow-hidden">
                        <p className="font-medium text-white truncate text-sm" title={question.question_text}>
                          {question.question_text}
                        </p>
                      </div>
                    </TableCell>
                    <TableCell className="w-[12%]">
                      <Badge variant={getQuestionTypeBadgeVariant(question.question_type)} size="sm">
                        {question.question_type?.replace('_', ' ') || 'N/A'}
                      </Badge>
                    </TableCell>
                    <TableCell className="w-[12%]">
                      <Badge variant={getDifficultyBadgeVariant(question.difficulty)} size="sm">
                        {question.difficulty || 'N/A'}
                      </Badge>
                    </TableCell>
                    <TableCell className="w-[8%] text-white/50">{question.points}</TableCell>
                    <TableCell className="w-[28%]">
                      <div className="flex items-center space-x-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          leftIcon={<Eye className="w-3 h-3" />}
                          onClick={() => handleViewQuestion(question)}
                        >
                          View
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          leftIcon={<Edit2 className="w-3 h-3" />}
                          onClick={() => handleEditQuestion(question)}
                        >
                          Edit
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          leftIcon={<Trash2 className="w-3 h-3" />}
                          onClick={() => handleDeleteQuestion(question)}
                          className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                        >
                          Delete
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {/* Pagination */}
            <div className="p-4 border-t border-white/[0.06] flex items-center justify-between">
              <div className="text-sm text-white/50">
                Page {page} of {totalPages}
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  leftIcon={<ChevronLeft className="w-4 h-4" />}
                  onClick={() => {
                    setPage((p) => Math.max(1, p - 1));
                    setSearchTerm(''); // Clear search when changing pages
                  }}
                  disabled={page === 1}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setPage((p) => Math.min(totalPages, p + 1));
                    setSearchTerm(''); // Clear search when changing pages
                  }}
                  disabled={page === totalPages}
                >
                  Next
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </div>
          </>
        ) : (
          <div className="text-center py-12">
            <HelpCircle className="w-12 h-12 text-white/30 mx-auto mb-2" />
            <p className="text-white/50 mb-3">No questions found</p>
            {searchTerm ? (
              <p className="text-sm text-white/40">Try adjusting your search or filters</p>
            ) : (
              <Button size="sm" leftIcon={<Plus className="w-4 h-4" />} onClick={handleAddQuestion}>
                Add Your First Question
              </Button>
            )}
          </div>
        )}
      </Card>

      {/* Add/Edit Question Modal */}
      {(isAddModalOpen || isEditModalOpen) && (
        <Modal
          isOpen={isAddModalOpen || isEditModalOpen}
          onClose={() => {
            setIsAddModalOpen(false);
            setIsEditModalOpen(false);
          }}
          title={isAddModalOpen ? 'Add New Question' : 'Edit Question'}
          size="xl"
        >
          <form onSubmit={isAddModalOpen ? handleSubmitCreate : handleSubmitUpdate}>
            <div className="space-y-4">
              <Input
                label="Question Text"
                required
                value={formData.question_text}
                onChange={(e) => setFormData({ ...formData, question_text: e.target.value })}
                placeholder="Enter your question"
              />

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Select
                  label="Question Type"
                  required
                  options={questionTypeOptions}
                  value={formData.question_type}
                  onChange={(e) => {
                    const newType = e.target.value as QuestionType;
                    setFormData({
                      ...formData,
                      question_type: newType,
                      options: newType === 'true_false'
                        ? [
                          { id: '1', text: 'True', is_correct: false },
                          { id: '2', text: 'False', is_correct: false },
                        ]
                        : formData.options
                    });
                  }}
                />
                <Select
                  label="Difficulty"
                  required
                  options={difficultyOptionsForForm}
                  value={formData.difficulty}
                  onChange={(e) => setFormData({ ...formData, difficulty: e.target.value as DifficultyLevel })}
                />
                <Input
                  label="Points"
                  type="number"
                  required
                  value={formData.points}
                  onChange={(e) => setFormData({ ...formData, points: parseInt(e.target.value) })}
                  min="1"
                />
              </div>

              {/* Options for MCQ and True/False */}
              {(formData.question_type === 'mcq' || formData.question_type === 'true_false') && (
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-white/70">
                    Options {formData.question_type === 'mcq' && '(check correct answer(s))'}
                  </label>
                  {formData.options.map((option, index) => (
                    <div key={option.id} className="flex items-center space-x-2">
                      <input
                        type={formData.question_type === 'true_false' ? 'radio' : 'checkbox'}
                        checked={option.is_correct}
                        onChange={() => handleCorrectChange(index)}
                        className="w-4 h-4 text-teal-500 bg-white/10 border-white/30 rounded focus:ring-teal-500"
                      />
                      <Input
                        value={option.text}
                        onChange={(e) => handleOptionChange(index, e.target.value)}
                        placeholder={`Option ${index + 1}`}
                        className="flex-1"
                        disabled={formData.question_type === 'true_false'}
                      />
                      {formData.question_type === 'mcq' && formData.options.length > 2 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeOption(index)}
                          className="text-red-400 hover:text-red-300"
                        >
                          Remove
                        </Button>
                      )}
                    </div>
                  ))}
                  {formData.question_type === 'mcq' && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      leftIcon={<Plus className="w-4 h-4" />}
                      onClick={addOption}
                    >
                      Add Option
                    </Button>
                  )}
                </div>
              )}

              <Input
                label="Explanation (Optional)"
                value={formData.explanation}
                onChange={(e) => setFormData({ ...formData, explanation: e.target.value })}
                placeholder="Explain the correct answer"
              />
            </div>
            <ModalFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setIsAddModalOpen(false);
                  setIsEditModalOpen(false);
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={createQuestionMutation.isPending || updateQuestionMutation.isPending}
              >
                {createQuestionMutation.isPending || updateQuestionMutation.isPending
                  ? 'Saving...'
                  : isAddModalOpen
                  ? 'Create Question'
                  : 'Update Question'}
              </Button>
            </ModalFooter>
          </form>
        </Modal>
      )}

      {/* View Question Modal */}
      <Modal
        isOpen={isViewModalOpen}
        onClose={() => setIsViewModalOpen(false)}
        title="View Question"
        size="lg"
      >
        {selectedQuestion && (
          <div className="space-y-6">
            {/* Question Type and Difficulty */}
            <div className="flex items-center space-x-4">
              <Badge variant={getQuestionTypeBadgeVariant(selectedQuestion.question_type)}>
                {selectedQuestion.question_type?.replace('_', ' ').toUpperCase() || 'N/A'}
              </Badge>
              <Badge variant={getDifficultyBadgeVariant(selectedQuestion.difficulty)}>
                {selectedQuestion.difficulty || 'N/A'}
              </Badge>
              <span className="text-sm text-white/50">{selectedQuestion.points} points</span>
            </div>

            {/* Question Text */}
            <div>
              <h3 className="text-sm font-medium text-white/70 mb-2">Question</h3>
              <div className="p-4 bg-white/5 border border-white/[0.06] rounded-lg">
                <p className="text-white whitespace-pre-wrap">{selectedQuestion.question_text}</p>
              </div>
            </div>

            {/* Options (for MCQ) */}
            {selectedQuestion.question_type === 'mcq' &&
             selectedQuestion.options && selectedQuestion.options.length > 0 && (
              <div>
                <h3 className="text-sm font-medium text-white/70 mb-2">Options</h3>
                <div className="space-y-2">
                  {selectedQuestion.options.map((option, index) => {
                    const isCorrect = selectedQuestion.correct_answer?.includes(option.id) ||
                      selectedQuestion.correct_answer?.includes(option.text);
                    return (
                      <div
                        key={option.id}
                        className={`p-3 rounded-lg border-2 ${
                          isCorrect
                            ? 'bg-emerald-500/10 border-emerald-500/50'
                            : 'bg-white/5 border-white/[0.06]'
                        }`}
                      >
                        <div className="flex items-start space-x-3">
                          <span className="flex-shrink-0 w-6 h-6 rounded-full bg-white/10 border-2 border-white/30 flex items-center justify-center text-sm font-medium text-white">
                            {String.fromCharCode(65 + index)}
                          </span>
                          <p className="flex-1 text-white">{option.text}</p>
                          {isCorrect && (
                            <Badge variant="success" size="sm">
                              Correct
                            </Badge>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* True/False Answer */}
            {selectedQuestion.question_type === 'true_false' && (
              <div>
                <h3 className="text-sm font-medium text-white/70 mb-2">Correct Answer</h3>
                <div className="p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-lg">
                  <p className="text-white font-medium capitalize">
                    {(() => {
                      const rawAnswer = Array.isArray(selectedQuestion.correct_answer)
                        ? selectedQuestion.correct_answer[0]
                        : selectedQuestion.correct_answer;
                      if (!rawAnswer) return 'Not specified';
                      // If answer is an option ID (e.g. "1" or "2"), resolve to option text
                      if (selectedQuestion.options && selectedQuestion.options.length > 0) {
                        const matchedOption = selectedQuestion.options.find(
                          (opt) => opt.id === rawAnswer
                        );
                        if (matchedOption) return matchedOption.text;
                      }
                      // If answer is already "true" or "false" text, capitalize it
                      return rawAnswer;
                    })()}
                  </p>
                </div>
              </div>
            )}

            {/* Correct Answer (for non-MCQ questions) */}
            {selectedQuestion.question_type !== 'mcq' &&
             selectedQuestion.question_type !== 'true_false' &&
             selectedQuestion.correct_answer && selectedQuestion.correct_answer.length > 0 && (
              <div>
                <h3 className="text-sm font-medium text-white/70 mb-2">Correct Answer</h3>
                <div className="p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-lg">
                  <p className="text-white whitespace-pre-wrap">
                    {selectedQuestion.correct_answer.join(', ')}
                  </p>
                </div>
              </div>
            )}

            {/* Explanation */}
            {selectedQuestion.explanation && (
              <div>
                <h3 className="text-sm font-medium text-white/70 mb-2">Explanation</h3>
                <div className="p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                  <p className="text-white whitespace-pre-wrap">{selectedQuestion.explanation}</p>
                </div>
              </div>
            )}

            {/* Tags */}
            {selectedQuestion.tags && selectedQuestion.tags.length > 0 && (
              <div>
                <h3 className="text-sm font-medium text-white/70 mb-2">Tags</h3>
                <div className="flex flex-wrap gap-2">
                  {selectedQuestion.tags.map((tag, index) => (
                    <Badge key={index} variant="secondary">
                      {tag}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Additional Info */}
            <div className="pt-4 border-t border-white/[0.06]">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-white/50">Created:</span>
                  <span className="ml-2 text-white">
                    {new Date(selectedQuestion.created_at).toLocaleDateString()}
                  </span>
                </div>
                <div>
                  <span className="text-white/50">Updated:</span>
                  <span className="ml-2 text-white">
                    {new Date(selectedQuestion.updated_at).toLocaleDateString()}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
        <ModalFooter>
          <Button
            variant="outline"
            onClick={() => setIsViewModalOpen(false)}
          >
            Close
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              setIsViewModalOpen(false);
              if (selectedQuestion) {
                handleEditQuestion(selectedQuestion);
              }
            }}
          >
            Edit Question
          </Button>
        </ModalFooter>
      </Modal>

      {/* Delete Question Modal */}
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        title="Delete Question"
        size="md"
      >
        <div className="space-y-4">
          <p className="text-white/70">
            Are you sure you want to delete this question? This action cannot be undone.
          </p>
          <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
            <p className="text-sm text-red-400">
              Warning: This question will be removed from all existing exams that use it.
            </p>
          </div>
        </div>
        <ModalFooter>
          <Button
            variant="outline"
            onClick={() => setIsDeleteModalOpen(false)}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleConfirmDelete}
            disabled={deleteQuestionMutation.isPending}
          >
            {deleteQuestionMutation.isPending ? 'Deleting...' : 'Delete Question'}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};
